import JWTDecode from 'jwt-decode';
import { SubmissionError } from 'redux-form';
import { setAxiosConfig } from 'redux-json-api';

export const SESSION_ACCESS_TOKEN_REFRESH_DEADLINE_SECONDS = 60;
export const SESSION_CURRENT_USER_SET = 'SESSION_CURRENT_USER_SET';
export const SESSION_SET_ACCESS_TOKEN = 'SESSION_SET_ACCESS_TOKEN';
export const SESSION_DESTROY = 'SESSION_DESTROY';
export const SESSION_RESUME = 'SESSION_RESUME';

export const SESSION_REFRESH_ACCESS_TOKEN = 'SESSION_REFRESH_ACCESS_TOKEN';
export const SESSION_REFRESH_ACCESS_TOKEN_DONE = 'SESSION_REFRESH_ACCESS_TOKEN_DONE';


export const authenticate = credentials => async (dispatch, getState) => {
  const { axiosConfig: { baseURL, headers } } = getState().api.endpoint;
  const apiEndpoint = `${baseURL}/obtain-json-web-token`;

  const resp = await fetch(apiEndpoint, {
    headers,
    method: 'POST',
    body: JSON.stringify({
      data: {
        type: 'obtain-json-web-token',
        attributes: credentials,
      },
    })
  });

  const result = await resp.json();

  if (result.errors) {
    throw new SubmissionError({ _error: 'Inloggen mislukt. E-mailadres of wachtwoord ongeldig.' });
  }

  return await dispatch(setAccessToken(result.data.token));
};

export const setAccessToken = accessToken => async (dispatch, getState) => {
  const { axiosConfig: { baseURL, headers } } = getState().api.endpoint;

  if (!accessToken) return;

  try {
    const exp = JWTDecode(accessToken).exp;
    const cur = Date.now() / 1000;

    if (exp && exp < cur) {
      console.error('[ATTV] setAccessToken token expired');
      return;
    }
  } catch (error) {
    console.error(`[ATTV] setAccessToken decode error=${error}`);
    return;
  }

  await dispatch(setAxiosConfig({
    baseURL,
    headers: {
      ...headers,
      'Authorization': `Bearer ${accessToken}`,
    },
  }));

  window.localStorage.accessToken = accessToken;

  return dispatch({ type: SESSION_SET_ACCESS_TOKEN, payload: accessToken });
};

export const destroy = () => {
  return async (dispatch) => {
    try {
      if (window && window.localStorage) {
        console.log('[ATTV] destroy removing accessToken from localStorage...');
        delete window.localStorage.accessToken;
      }
    } catch (error) {
      console.error('[ATTV] destroy ERROR: could not delete from localStorage');
    }

    await dispatch({ type: SESSION_DESTROY });
  };
};

export const resume = () => async (dispatch, getState) => {
  const accessToken = window.localStorage.accessToken;

  if (!accessToken) {
    console.log('[ATTV] resume no accessToken...');
    await dispatch(destroy());
    return;
  }

  try {
    const { exp } = JWTDecode(accessToken);
    const cur = Date.now() / 1000;

    if (exp && exp < cur) {
      console.error('[ATTV] resume accessToken expired ');
      await dispatch(destroy());
      return;
    }
  } catch (error) {
    console.error(`[ATTV resume accessToken decode error=${error}`);
    await dispatch(destroy());
    return;
  }

  await dispatch(setAccessToken(accessToken));
  await dispatch(verifyJSONWebToken());
};

export const verifyJSONWebToken = () => async (dispatch, getState) => {
  const { axiosConfig: { baseURL, headers } } = getState().api.endpoint;
  const apiEndpoint = `${baseURL}/verify-json-web-token`;
  const token = getState().session.accessToken;

  const resp = await fetch (apiEndpoint, {
    headers,
    method: 'POST',
    body: JSON.stringify({
      data: {
        type: 'verify-json-web-token',
        attributes: {
          token
        }
      }
    })
  });

  const result = await resp.json();
  if (result.errors) {
    return dispatch(destroy())
  }
};

export const refreshToken = async (dispatch, getState) => {
  const { host: apiHost, path: apiPath, headers } = getState().api.endpoint;
  const apiEndpoint = `${apiHost}${apiPath}/refresh-json-web-token`;
  const token = getState().session.accessToken;

  const refreshTokenPromise = new Promise((resolve, reject) => {
    fetch(apiEndpoint, {
      headers,
      method: 'POST',
      body: JSON.stringify({
        data: {
          type: 'refresh-json-web-token',
          attributes: {
            token,
          },
        },
      }),
    }).then(async res => {
      const decoded = await res.json();

      dispatch({
        type: SESSION_REFRESH_ACCESS_TOKEN_DONE,
      });

      if (res.ok) {
        if (decoded.data && decoded.data.token) {
          dispatch(setAccessToken(decoded.data.token));

          resolve(decoded.data.token);
        } else {
          dispatch(destroy());

          reject({message: 'refresh-json-web-token returned no token!'});
        }
      } else {
        dispatch(destroy());

        if (decoded.errors && decoded.errors.non_field_errors) {
          reject({message: 'refreshToken failed: ' + decoded.errors.non_field_errors.join(' ')});
        } else {
          reject({message: 'refreshToken failed: ' + res.statusText});
        }
      }
    }).catch(exc => {
      console.log('refreshToken error:',exc);
      reject(exc);
    });
  });

  await dispatch({
    type: SESSION_REFRESH_ACCESS_TOKEN,
    payload: refreshTokenPromise,
  });

  return refreshTokenPromise;
};

/**
 * Redux middleware that refreshes the access token if necessary on every API
 * request.
 */
export const refreshAccessTokenMiddleware = ({ dispatch, getState }) => next => action => {
  if (typeof action === 'function') {
    const { session: { accessToken, refreshAccessTokenPromise } } = getState();

    if (accessToken) {
      const exp = JWTDecode(accessToken).exp;
      const cur = Date.now() / 1000;

      if (exp) {
        if (exp < cur) return dispatch(destroy());

        if (exp < (cur + SESSION_ACCESS_TOKEN_REFRESH_DEADLINE_SECONDS)) {
          if (refreshAccessTokenPromise)
            return refreshAccessTokenPromise.then(() => next(action));

          return refreshToken()(dispatch, getState).then(() => next(action));
        }
      }
    }
  }

  return next(action);
};

